import axios from "./axios";
import { downloadHsePdfBlob, raiseHsePdfRequestError } from "./hse/downloadPdf";

export const chemicalInventoryApi = {
  // Get all chemical inventories with filters
  getChemicalInventories: async (params = {}) => {
    const response = await axios.get("/chemical_inventories", {
      params,
    });
    return response.data;
  },

  // Get a single chemical inventory by ID
  getChemicalInventory: async (id) => {
    const response = await axios.get(`/chemical_inventories/${id}`);
    return response.data;
  },

  // Create a new chemical inventory
  createChemicalInventory: async (data) => {
    const response = await axios.post("/chemical_inventories", {
      chemical_inventory: data,
    });
    return response.data;
  },

  // Update an existing chemical inventory
  updateChemicalInventory: async (id, data) => {
    return axios
      .put(`/chemical_inventories/${id}`, {
        chemical_inventory: data,
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error.response?.data || error);
      });
  },

  // Delete a chemical inventory
  deleteChemicalInventory: async (id) => {
    const response = await axios.delete(`/chemical_inventories/${id}`);
    return response.data;
  },

  analyzeDocument: async (input) => {
    const file = input instanceof File ? input : input?.file;
    const documentId =
      input instanceof File
        ? undefined
        : typeof input === "object"
        ? input?.document_id
        : input;

    if (typeof FormData !== "undefined" && file instanceof File) {
      const formData = new FormData();
      formData.append("file", file);
      if (documentId) formData.append("document_id", documentId);
      const response = await axios.post(
        "/chemical_inventories/analyze_document",
        formData
      );
      return response.data;
    }

    const response = await axios.post("/chemical_inventories/analyze_document", {
      document_id: documentId,
    });
    return response.data;
  },

  // Create chemical inventory from product (collection URL)
  createFromProduct: async (productId) => {
    const response = await axios.post(
      "/chemical_inventories/create_from_product",
      { product_id: productId }
    );
    return response.data;
  },

  createRevision: async (id) => {
    const response = await axios.post(`/chemical_inventories/${id}/revisions`);
    return response.data;
  },

  publishReview: async (id, { change_summary, next_review_date } = {}) => {
    const payload = { change_summary };
    if (next_review_date) payload.next_review_date = next_review_date;
    const response = await axios.post(
      `/chemical_inventories/${id}/publish_review`,
      payload
    );
    return response.data;
  },

  getRevisions: async (id) => {
    const response = await axios.get(`/chemical_inventories/${id}/revisions`);
    return response.data;
  },

  exportPdf: async (id, filename) => {
    try {
      const response = await axios.get(`/chemical_inventories/${id}/export_pdf`, {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      });
      await downloadHsePdfBlob(response.data, filename || `stoffkartotek-${id}.pdf`);
    } catch (error) {
      await raiseHsePdfRequestError(error);
    }
  },

  getSettings: async () => {
    const response = await axios.get("/hse/chemical_inventory_settings");
    return response.data;
  },

  updateSettings: async (payload) => {
    const response = await axios.put("/hse/chemical_inventory_settings", {
      chemical_inventory_settings: payload,
    });
    return response.data;
  },

  // Add document to chemical inventory
  addDocument: async (id, documentData) => {
    const response = await axios.post(
      `/chemical_inventories/${id}/add_document`,
      documentData
    );
    return response.data;
  },

  // Remove document from chemical inventory
  removeDocument: async (id, documentId) => {
    return axios
      .delete(`/chemical_inventories/${id}/remove_document`, {
        data: { document_id: documentId },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error.response?.data || error);
      });
  },

  // Add note to chemical inventory
  addNote: async (id, noteData) => {
    return axios
      .post(`/chemical_inventories/${id}/add_note`, noteData)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error.response?.data || error);
      });
  },

  // Get statistics
  getStatistics: async () => {
    return axios
      .get("/chemical_inventories/statistics")
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return Promise.reject(error.response?.data || error);
      });
  },
};
